// Client-side image compression, run before a photo/document ever hits the
// wire. Mirrors the backend's compress_uploaded_image (api/utils/media_optimization.py):
// same 1600px cap and ~0.82 quality, so a large phone photo shrinks before
// upload instead of only after it reaches the server. Compressing server-side
// alone doesn't help slow/limited connections, since the full original file
// still has to be sent before the server can touch it.

export const IMAGE_MAX_DIMENSION = 1600;
export const IMAGE_QUALITY = 0.82;

const COMPRESSIBLE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const MIN_COMPRESS_SIZE = 300 * 1024;

/**
 * Resize/recompress an image File in the browser before upload. Non-image
 * files (PDFs, spreadsheets, etc.) are returned untouched — there's no
 * practical browser-side equivalent to the server's Ghostscript PDF pass.
 * Also returns the original file unchanged if compression fails for any
 * reason, or if it wouldn't actually be smaller.
 *
 * @param {File} file
 * @param {{maxDimension?: number, quality?: number}} [options]
 * @returns {Promise<File>}
 */
export async function compressImageFile(file, options = {}) {
  // Small files (icons, signatures, thumbnails) aren't worth decoding.
  if (!file || !COMPRESSIBLE_TYPES.has(file.type) || file.size < MIN_COMPRESS_SIZE) {
    return file;
  }

  const maxDimension = options.maxDimension ?? IMAGE_MAX_DIMENSION;
  const quality = options.quality ?? IMAGE_QUALITY;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));

    if (scale === 1 && file.size < 1.5 * 1024 * 1024) {
      // Already small enough and under the resolution cap — not worth re-encoding.
      bitmap.close?.();
      return file;
    }

    const targetWidth = Math.round(bitmap.width * scale);
    const targetHeight = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close?.();

    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, outputType, quality));

    if (!blob || blob.size >= file.size) {
      return file;
    }

    return new File([blob], file.name, { type: outputType, lastModified: Date.now() });
  } catch (e) {
    console.warn('compressImageFile: failed to compress, using original file', e);
    return file;
  }
}

/**
 * Returns a copy of `formData` with every image File compressed (in parallel).
 * Entry order and repeated keys are preserved; non-image entries pass through.
 * Used by the global axios request interceptor so individual upload sites
 * don't each have to call compressImageFile.
 *
 * @param {FormData} formData
 * @returns {Promise<FormData>}
 */
export async function compressFormDataImages(formData) {
  const entries = Array.from(formData.entries());
  if (!entries.some(([, v]) => v instanceof File && COMPRESSIBLE_TYPES.has(v.type))) {
    return formData;
  }

  const compressed = await Promise.all(
    entries.map(([, v]) => (v instanceof File ? compressImageFile(v) : v))
  );

  const result = new FormData();
  entries.forEach(([key], i) => {
    const v = compressed[i];
    if (v instanceof File) result.append(key, v, v.name);
    else result.append(key, v);
  });
  return result;
}
